import React from 'react';
import { useHistory, Link } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { H2, Page, LayoutSingleColumn, Button } from '../../components';
import { apiBaseUrl } from '../../util/api';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './ContactPage.module.css';

const ContactPage = props => {
  const intl = useIntl();
  const history = useHistory();
  const topics = [
    { id: 'rental', intlId: 'ContactPage.topicRental', defaultMessage: 'Rental queries' },
    { id: 'billing', intlId: 'ContactPage.topicBilling', defaultMessage: 'Billing queries' },
    { id: 'technical', intlId: 'ContactPage.topicTechnical', defaultMessage: 'Technical queries' },
    { id: 'other', intlId: 'ContactPage.otherTopics', defaultMessage: 'Other topics' },
  ];

  const handleSubmit = async e => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const values = Object.fromEntries(formData.entries());
    const { name, email, topic, message } = values;

    let responseMailSupport;
    let responseMailUser;

    // Send e-mail to ALOKA support. Only for PROD environments.
    if (process.env.REACT_APP_ALOKA_ENV === 'production') {
      const msgSupport = `
     <p>Tienes una nueva consulta de <strong>${name} (${email})</strong>:
     <p>${message}</p>
    `;
      responseMailSupport = await fetch(apiBaseUrl() + '/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nameFrom: 'Buzón de consultas de Aloka',
          from: 'hola@aloka.app',
          nameTo: 'Soporte de Aloka',
          to: 'hola@aloka.app',
          subject: intl.formatMessage({
            id: topics.find(t => t.id === topic).intlId,
            defaultMessage: topics.find(t => t.id === topic).defaultMessage,
          }),
          html: msgSupport,
        }),
      });
    }

    // Send e-mail to user.
    const msgUser = `
      <p>Hola <strong>${name}</strong>,</p>
      <p>Nuestro soporte ha recibido su consulta. Intentaremos atenderla en la mayor brevedad posible.</p>
      <p>El equipo de Aloka</p>
      <p><strong>Copia de su consulta:</strong></p>
      <p style="font-style: italic;">${message}</p>
    `;
    responseMailUser = await fetch(apiBaseUrl() + '/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        nameTo: name,
        to: email,
        nameFrom: 'Soporte de Aloka',
        from: 'hola@aloka.app',
        subject:
          process.env.REACT_APP_ALOKA_ENV === 'development'
            ? '[TEST] Subject'
            : intl.formatMessage({
                id: topics.find(t => t.id === topic).intlId,
                defaultMessage: topics.find(t => t.id === topic).defaultMessage,
              }),
        html: msgUser,
      }),
    });

    const path = window.location.pathname;
    const currentLang = path.split('/')[1];
    if (
      process.env.REACT_APP_ALOKA_ENV === 'production' &&
      responseMailSupport.ok &&
      responseMailUser.ok
    ) {
      history.push(`/${currentLang}/contact/success`);
    } else if (process.env.REACT_APP_ALOKA_ENV === 'development' && responseMailUser.ok) {
      history.push(`/${currentLang}/contact/success`);
    } else {
      // Do nothing for now.
    }
  };

  return (
    <Page
      title="Contact"
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ContactPage',
        name: 'Contact',
      }}
    >
      <LayoutSingleColumn
        topbar={
          <>
            <TopbarContainer currentPage="ContactPage" />
          </>
        }
        footer={<FooterContainer />}
        mainColumnClassName={css.mainColumn}
      >
        <div className={css.root}>
          <H2 as="h1" className={css.title}>
            <FormattedMessage id="ContactPage.heading" defaultMessage="Contact us" />
          </H2>
          <p className={css.subtitle}>
            <FormattedMessage
              id="ContactPage.subtitle"
              defaultMessage="Got a question? We would love to hear from you. Send us a message and we'll respond as soon as possible."
            />
          </p>
          <div className={css.contentwrapper}>
            <form className={css.form} onSubmit={handleSubmit}>
              <label htmlFor="topic" className={css.label}>
                <FormattedMessage id="ContactPage.topic" defaultMessage="Select a topic" />
              </label>
              <select id="topic" name="topic" className={css.select} required defaultValue="">
                <option value="" disabled hidden>
                  -
                </option>

                {topics.map(topic => (
                  <option key={topic.id} value={topic.id}>
                    {intl.formatMessage({
                      id: topic.intlId,
                      defaultMessage: topic.defaultMessage,
                    })}
                  </option>
                ))}
              </select>

              <label htmlFor="name" className={css.label}>
                <FormattedMessage id="ContactPage.name" defaultMessage="Name" />
              </label>
              <input type="text" id="name" name="name" className={css.input} required />

              <label htmlFor="email" className={css.label}>
                <FormattedMessage id="ContactPage.email" defaultMessage="Email address" />
              </label>
              <input type="email" id="email" name="email" className={css.input} required />

              <label htmlFor="message" className={css.label}>
                <FormattedMessage id="ContactPage.msg" defaultMessage="Message" />
              </label>
              <textarea
                id="message"
                name="message"
                rows="5"
                className={css.textarea}
                required
              ></textarea>

              <div className={css.checkboxWrapper}>
                <label className={css.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="privacy"
                    value="true"
                    className={css.checkbox}
                    required
                  />
                  <span>
                    <FormattedMessage
                      id="ContactPage.privacyPolicy"
                      defaultMessage="I accept the {privacyPolicyLink}"
                      values={{
                        privacyPolicyLink: (
                          <Link to="privacy-policy">
                            <FormattedMessage
                              id="ContactPage.privacyPolicyLabel"
                              defaultMessage="privacy policy"
                            />
                          </Link>
                        ),
                      }}
                    />
                  </span>
                </label>
              </div>
              <div className={css.checkboxWrapper}>
                <label className={css.checkboxLabel}>
                  <input type="checkbox" name="newsletter" value="true" className={css.checkbox} />
                  <span>
                    <FormattedMessage
                      id="ContactPage.newsletter"
                      defaultMessage="I agree to receive the newsletter"
                    />
                  </span>
                </label>
              </div>

              <Button
                className={css.submitButton}
                type="submit"
                inProgress={false}
                disabled={false}
              >
                <FormattedMessage id="ContactPage.submitButton" defaultMessage="Send message" />
              </Button>
            </form>
            <div className={css.infoSection}>
              <div className={css.mapWrapper}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2903.2072871832083!2d-1.9785149248272897!3d43.309919971120415!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd51af64b850afc3%3A0x6a27ad7d31073bb8!2saloka!5e0!3m2!1ses!2ses!4v1769957333590!5m2!1ses!2ses"
                  width="600"
                  height="450"
                  allowFullScreen
                  loading="lazy"
                  className={css.map}
                ></iframe>
              </div>
              <div className={css.legalInForm}>
                <p className={css.legalTitle}>
                  <FormattedMessage
                    id="ContactPage.legalTitle"
                    defaultMessage="BASIC DATA ON DATA PROTECTION"
                  />
                </p>
                <p className={css.legalText}>
                  <FormattedMessage
                    id="ContactPage.responsible"
                    defaultMessage="RESPONSIBLE: ALOKA.APP, SL"
                  />
                  <br />
                  <FormattedMessage
                    id="ContactPage.purpose"
                    defaultMessage="PURPOSE: Management of the received enquiries and provision of electronic communication services."
                  />
                  <br />
                  <FormattedMessage
                    id="ContactPage.recipients"
                    defaultMessage="RECIPIENTS: Data will not be transferred to third parties, except where required by law."
                  />
                  <br />
                  <FormattedMessage
                    id="ContactPage.rights"
                    defaultMessage="RIGHTS: The user may exercise, at any time, freely and voluntarily, the rights of access, rectification, cancellation and opposition that correspond by law."
                  />
                </p>
                <p className={css.legalText}>
                  <FormattedMessage
                    id="ContactPage.moreInfo"
                    defaultMessage="Further information on {dataProtectionLink} can be found on our website."
                    values={{
                      dataProtectionLink: (
                        <Link to="privacy-policy">
                          <FormattedMessage
                            id="ContactPage.dataProtectionLabel"
                            defaultMessage="data protection"
                          />
                        </Link>
                      ),
                    }}
                  />
                </p>
              </div>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default ContactPage;
